import { computed, ref, watchEffect } from 'vue';
import { parseColor } from '@vp/render';
import { settings } from './platform';

/** Theme (light / dark / as the system) and the user's accent color for buttons and highlights. */
export const ACCENTS = ['#5e9cff', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0d9488', '#0891b2', '#475569', '#fde047', '#a5f3fc'];

const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
const systemDark = ref(!!media?.matches);
media?.addEventListener('change', (e) => (systemDark.value = e.matches));

export const isDark = computed(() => settings.theme === 'dark' || (settings.theme === 'system' && systemDark.value));

/** The appearance switch is on, or the system asked for less motion. */
export function reduceEffects(): boolean {
  return settings.noMotion || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
}
/** The canvas is shown dark (colors inverted with their hue kept) in the dark theme, unless turned off. */
export const canvasDark = computed(() => isDark.value && settings.darkCanvas);

type Rgb = [number, number, number];
const DARK_TEXT = '#0f172a';
const LIGHT_TEXT = '#ffffff';
/** Panel backgrounds of the two themes (styles.css --panel). */
const PANEL = { light: '#ffffff', dark: '#172033' };

function rgb(hex: string): Rgb {
  const [r, g, b] = parseColor(hex);
  return [r, g, b];
}

function hex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('');
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Relative luminance (WCAG 2). */
function luminance(c: Rgb): number {
  const [r, g, b] = c.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two colors (1…21). */
export function contrast(a: string, b: string): number {
  const la = luminance(rgb(a));
  const lb = luminance(rgb(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Text and icons on an accent background: dark on light accents, white on dark ones — whichever reads better. */
export function onAccent(accent: string): string {
  return contrast(accent, DARK_TEXT) > contrast(accent, LIGHT_TEXT) ? DARK_TEXT : LIGHT_TEXT;
}

/**
 * Accent-colored text and icons (active pills, the selected section) that stay readable: the accent
 * itself when it contrasts enough with the light tint behind it, otherwise darkened (light theme)
 * or lightened (dark theme) just enough — a yellow accent gives dark-olive text, not yellow on white.
 */
export function accentInk(accent: string, dark: boolean): string {
  const a = rgb(accent);
  const panel = rgb(dark ? PANEL.dark : PANEL.light);
  // the tint behind active elements (styles.css --accent-soft)
  const soft = hex(mix(panel, a, dark ? 0.24 : 0.13));
  const target: Rgb = dark ? [1, 1, 1] : [0, 0, 0];
  for (let t = 0; t <= 1.0001; t += 0.04) {
    const c = hex(mix(a, target, t));
    if (contrast(c, soft) >= 4.5 && contrast(c, hex(panel)) >= 4.5) return c;
  }
  return dark ? LIGHT_TEXT : DARK_TEXT;
}

/**
 * Accent text and icons actually used. A hand-picked ink that disappears on the panel
 * (a dark ink in the dark theme) is replaced by the automatic readable one.
 */
export function effectiveInk(accent: string, chosen: string, dark: boolean): string {
  const auto = accentInk(accent, dark);
  if (!chosen) return auto;
  const panel = dark ? PANEL.dark : PANEL.light;
  return contrast(chosen, panel) >= 4.5 ? chosen : auto;
}

/** The colors in effect: the user's choice, or the automatic one. */
export const accentColors = computed(() => ({
  onAccent: settings.onAccent || onAccent(settings.accent),
  ink: effectiveInk(settings.accent, settings.accentInk, isDark.value),
}));

export function installTheme(): void {
  watchEffect(() => {
    const el = document.documentElement;
    const c = accentColors.value;
    el.dataset.theme = isDark.value ? 'dark' : 'light';
    if (settings.noMotion) el.dataset.effects = 'off';
    else delete el.dataset.effects;
    const glass = Number(settings.glass);
    const seeThrough = settings.noMotion ? 0 : Number.isFinite(glass) ? Math.min(100, Math.max(0, glass)) : 82;
    el.style.setProperty('--glass', String(seeThrough));
    // text must stay readable over any canvas color: the glass never gets clearer than `floor` (dark UI: light text on a possibly light canvas)
    // the slider still moves over its whole length: 0 → fully solid, 100 → as clear as stays readable
    const floor = isDark.value ? 84 : 55;
    const tint = Math.round(100 - (seeThrough * (100 - floor)) / 100);
    el.style.setProperty('--glass-tint', `${tint}%`);
    el.style.setProperty('--accent', settings.accent);
    el.style.setProperty('--on-accent', c.onAccent);
    el.style.setProperty('--accent-text', c.ink);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDark.value ? '#0b1220' : '#f1f5f9');
    try {
      localStorage.setItem('vp-theme', JSON.stringify({ dark: isDark.value, accent: settings.accent, onAccent: c.onAccent, accentText: c.ink }));
    } catch {
      /* storage blocked: the theme is applied after load */
    }
  });
}
